import React, { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { DashboardLayout } from '@xanectech/components/layout';
import { Loading } from '@xanectech/components/shared';
import {
  PortalAccessDeniedPage,
  RequireAuthWrapper,
  useAuth,
  usePortalLoginOutcome,
} from '@xanectech/auth';
import { useAdminDashboardConfig } from './lib/useAdminDashboardConfig';
import { PrototypeRoutes } from './PrototypeRoutes';
import { lazyNamedExport } from '@xanectech/lib/utils/lazyNamedExport';

/** Standard Pages */
const DashboardPage = lazyNamedExport(
  () => import('./pages/AdminDashboardPage'),
  'AdminDashboardPage',
);

const NotFound = lazyNamedExport(
  () => import('@xanectech/components/shared'),
  'NotFoundPage',
);

const AuthPage = lazyNamedExport(() => import('@xanectech/auth'), 'AuthPage');

const LogoutPage = lazyNamedExport(
  () => import('./pages/LogoutPage'),
  'LogoutPage',
);

/** Analytics Pages */
const AnalyticsPage = lazyNamedExport(
  () => import('./pages/Analytics/AnalyticsPage'),
  'AnalyticsPage',
);

const SecurityAnalyticsPage = lazyNamedExport(
  () => import('./pages/Analytics/SecurityAnalyticsPage'),
  'SecurityAnalyticsPage',
);

// const HelpAnalyticsPage = lazyNamedExport(
//   () => import('./pages/Analytics/HelpAnalyticsPage'),
//   'HelpAnalyticsPage',
// );

const LoginHistoryPage = lazyNamedExport(
  () => import('./pages/Analytics/LoginHistoryPage'),
  'LoginHistoryPage',
);

/** Management Pages */
const CompanyManagementPage = lazyNamedExport(
  () => import('./pages/Company/CompanyManagementPage'),
  'CompanyManagementPage',
);

const CompanyDetailsPage = lazyNamedExport(
  () => import('./pages/Company/CompanyDetailsPage'),
  'CompanyDetailsPage',
);

const CompanyEditPage = lazyNamedExport(
  () => import('./pages/Company/CompanyEditPage'),
  'CompanyEditPage',
);

const UserManagementPage = lazyNamedExport(
  () => import('./pages/Users/UserManagementPage'),
  'UserManagementPage',
);

const UserEditPage = lazyNamedExport(
  () => import('./pages/Users/UserEditPage'),
  'UserEditPage',
);

const UserDetailsPage = lazyNamedExport(
  () => import('./pages/Users/UserDetailsPage'),
  'UserDetailsPage',
);

const MessagesPage = lazyNamedExport(
  () => import('./pages/AdminMessagesPage'),
  'AdminMessagesPage',
);

const LogManagementPage = lazyNamedExport(
  () => import('./pages/LogManagementPage'),
  'LogManagementPage',
);

const GeoManagementPage = lazyNamedExport(
  () => import('./pages/Geo/GeoManagementPage'),
  'GeoManagementPage',
);

const PrototypeManagementPage = lazyNamedExport(
  () => import('./pages/Prototype/PrototypeManagementPage'),
  'PrototypeManagementPage',
);

const SubscriptionManagementPage = lazyNamedExport(
  () => import('./pages/subscription/SubscriptionManagementPage'),
  'SubscriptionManagementPage',
);

const EmailTemplateManagementPage = lazyNamedExport(
  () => import('./pages/EmailTemplate/EmailTemplateManagementPage'),
  'EmailTemplateManagementPage',
);

const EmailTemplateDetailsPage = lazyNamedExport(
  () => import('./pages/EmailTemplate/EmailTemplateDetailsPage'),
  'EmailTemplateDetailsPage',
);

const EmailTemplateEditPage = lazyNamedExport(
  () => import('./pages/EmailTemplate/EmailTemplateEditPage'),
  'EmailTemplateEditPage',
);

const DatabaseManagementPage = lazyNamedExport(
  () => import('./pages/Database/DatabaseManagementPage'),
  'DatabaseManagementPage',
);

const IdeaManagementPage = lazyNamedExport(
  () => import('./pages/IdeaManagementPage'),
  'IdeaManagementPage',
);

const RoadmapManagementPage = lazyNamedExport(
  () => import('./pages/RoadmapManagementPage'),
  'RoadmapManagementPage',
);

/** Settings Pages */
const SettingsPage = lazyNamedExport(
  () => import('./pages/AdminSettingsPage'),
  'AdminSettingsPage',
);

/** Data structure management pages */
const DataStructureManagementPage = lazyNamedExport(
  () => import('./pages/DataCategories/DataStructureManagementPage'),
  'DataStructureManagementPage',
);

/** Help Pages */
const HelpManagementPage = lazyNamedExport(
  () => import('./pages/Help/HelpManagementPage'),
  'HelpManagementPage',
);

const publicRoutes: string[] = ['/auth', '/logout', '/reset-password'];

// Loading fallback component
const RouteLoadingFallback = () => <Loading spinner='Xanec' />;

function AdminRoutes(): React.ReactElement {
  const dashboard = useAdminDashboardConfig();
  const { portal, user } = useAuth();

  usePortalLoginOutcome({
    currentRole:
      dashboard.status === 'access-denied' ? dashboard.currentRole : null,
    denialReason:
      dashboard.status === 'access-denied' ? dashboard.reason : undefined,
    email: user?.email,
    outcome:
      dashboard.status === 'authenticated'
        ? 'success'
        : dashboard.status === 'access-denied'
          ? 'denied'
          : 'pending',
    portal,
    requestedPath: `${window.location.pathname}${window.location.search}`,
    userId: user?.id,
  });

  if (dashboard.status === 'loading') {
    return <Loading spinner='Xanec' />;
  }

  if (dashboard.status === 'access-denied') {
    return <PortalAccessDeniedPage currentRole={dashboard.currentRole} />;
  }

  if (dashboard.status === 'unauthenticated') {
    return (
      <RequireAuthWrapper publicRoutes={publicRoutes}>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route
              path='/auth'
              element={<AuthPage allowSignup={false} redirectPath='/' />}
            />
            <Route path='/logout' element={<LogoutPage />} />
            <Route path='*' element={<NotFound />} />
          </Routes>
        </Suspense>
      </RequireAuthWrapper>
    );
  }

  return (
    <RequireAuthWrapper publicRoutes={publicRoutes}>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route
            path='/'
            element={<DashboardLayout config={dashboard.config!} />}
          >
            <Route index element={<DashboardPage />} />
            <Route path='analytics' element={<AnalyticsPage />} />
            <Route
              path='analytics/security'
              element={<SecurityAnalyticsPage />}
            />
            <Route path='users' element={<UserManagementPage />} />
            <Route path='users/:userId/edit' element={<UserEditPage />} />
            <Route path='users/new' element={<UserEditPage />} />
            <Route path='users/:userId' element={<UserDetailsPage />} />
            {/* <Route path='analytics/help' element={<HelpAnalyticsPage />} /> */}

            <Route path='messages' element={<MessagesPage />} />
            <Route path='prototypes' element={<PrototypeManagementPage />} />
            <Route
              path='subscriptions'
              element={<SubscriptionManagementPage />}
            />
            {...PrototypeRoutes()}
            <Route
              path='data-structures'
              element={<DataStructureManagementPage />}
            />
            <Route path='login-history' element={<LoginHistoryPage />} />
            <Route path='companies' element={<CompanyManagementPage />} />
            <Route
              path='companies/:companyId'
              element={<CompanyDetailsPage />}
            />
            <Route
              path='companies/:companyId/settings'
              element={<CompanyEditPage />}
            />
            <Route path='ideas' element={<IdeaManagementPage />} />
            <Route path='roadmap' element={<RoadmapManagementPage />} />
            <Route path='companies/create' element={<CompanyEditPage />} />
            <Route path='geo' element={<GeoManagementPage />} />
            <Route path='logs' element={<LogManagementPage />} />
            <Route
              path='email-templates'
              element={<EmailTemplateManagementPage />}
            />
            <Route
              path='email-templates/:templateId'
              element={<EmailTemplateDetailsPage />}
            />
            <Route
              path='email-templates/:templateId/edit'
              element={<EmailTemplateEditPage />}
            />
            <Route
              path='email-templates/create'
              element={<EmailTemplateEditPage />}
            />
            <Route path='settings' element={<SettingsPage />} />
            <Route path='database' element={<DatabaseManagementPage />} />
            <Route path='help-management' element={<HelpManagementPage />} />
            {/* <Route
                path='companies/:companyId/users'
                element={<CompanyUsersPage />}
              />
              <Route
                path='companies/:companyId/analytics'
                element={<CompanyAnalyticsPage />}
              /> */}
          </Route>
          <Route
            path='/auth'
            element={<AuthPage allowSignup={false} redirectPath='/' />}
          />
          <Route path='/logout' element={<LogoutPage />} />
          <Route path='*' element={<NotFound />} />
        </Routes>
      </Suspense>
    </RequireAuthWrapper>
  );
}
export { AdminRoutes };
